import React, { useState, useEffect } from 'react';
import { sharingApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Label } from '../ui/label';
import { Copy, Link2, Trash2, ExternalLink, Calendar, Eye } from 'lucide-react';
import { toast } from 'sonner';

interface ShareLink {
  id: string;
  token: string;
  created_by: string;
  expires_at: string | null;
  access_count: number;
  max_access_count: number | null;
  is_active: boolean;
  created_at: string;
}

interface ShareModalProps {
  workflowId: string;
  workflowName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function ShareModal({
  workflowId,
  workflowName,
  open,
  onOpenChange,
}: ShareModalProps) {
  const [shareLinks, setShareLinks] = useState<ShareLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [expiresIn, setExpiresIn] = useState<string>('never');
  const [maxAccess, setMaxAccess] = useState<string>('unlimited');

  useEffect(() => {
    if (open) {
      loadShareLinks();
    }
  }, [open, workflowId]);

  const loadShareLinks = async () => {
    try {
      const response = await sharingApi.getShareLinks(workflowId);
      setShareLinks(response.data);
    } catch (error) {
      toast.error('Failed to load share links');
    }
  };

  const handleCreateShareLink = async () => {
    setLoading(true);
    try {
      const data: any = {};
      
      // Set expiration
      if (expiresIn !== 'never') {
        const expiresAt = new Date();
        switch (expiresIn) {
          case '1hour':
            expiresAt.setHours(expiresAt.getHours() + 1);
            break;
          case '1day':
            expiresAt.setDate(expiresAt.getDate() + 1);
            break;
          case '7days':
            expiresAt.setDate(expiresAt.getDate() + 7);
            break;
          case '30days':
            expiresAt.setDate(expiresAt.getDate() + 30);
            break;
        }
        data.expires_at = expiresAt.toISOString();
      }

      // Set max access count
      if (maxAccess !== 'unlimited') {
        data.max_access_count = parseInt(maxAccess);
      }

      await sharingApi.createShareLink(workflowId, data);
      toast.success('Share link created successfully');
      loadShareLinks();
      setExpiresIn('never');
      setMaxAccess('unlimited');
    } catch (error) {
      toast.error('Failed to create share link');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeShareLink = async (shareId: string) => {
    if (!confirm('Are you sure you want to revoke this share link?')) {
      return;
    }
    try {
      await sharingApi.revokeShareLink(workflowId, shareId);
      toast.success('Share link revoked successfully');
      loadShareLinks();
    } catch (error) {
      toast.error('Failed to revoke share link');
    }
  };

  const copyShareLink = (token: string) => {
    const url = `${window.location.origin}/shared/${token}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
  };

  const openShareLink = (token: string) => {
    const url = `${window.location.origin}/shared/${token}`;
    window.open(url, '_blank');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isExpired = (expiresAt: string | null) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const isMaxAccessReached = (link: ShareLink) => {
    if (!link.max_access_count) return false;
    return link.access_count >= link.max_access_count;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Share Workflow</DialogTitle>
          <DialogDescription>
            Create a public link to share "{workflowName}" with others
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Create New Share Link */}
          <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
            <h3 className="font-semibold text-sm">Create New Share Link</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="expires">Expires In</Label>
                <Select value={expiresIn} onValueChange={setExpiresIn}>
                  <SelectTrigger id="expires">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="never">Never</SelectItem>
                    <SelectItem value="1hour">1 Hour</SelectItem>
                    <SelectItem value="1day">1 Day</SelectItem>
                    <SelectItem value="7days">7 Days</SelectItem>
                    <SelectItem value="30days">30 Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="maxAccess">Max Access Count</Label>
                <Select value={maxAccess} onValueChange={setMaxAccess}>
                  <SelectTrigger id="maxAccess">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unlimited">Unlimited</SelectItem>
                    <SelectItem value="1">1 time</SelectItem>
                    <SelectItem value="5">5 times</SelectItem>
                    <SelectItem value="10">10 times</SelectItem>
                    <SelectItem value="50">50 times</SelectItem>
                    <SelectItem value="100">100 times</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button
              onClick={handleCreateShareLink}
              disabled={loading}
              className="w-full"
            >
              <Link2 className="w-4 h-4 mr-2" />
              Create Share Link
            </Button>
          </div>

          {/* Existing Share Links */}
          <div className="space-y-3">
            <h3 className="font-semibold text-sm">Active Share Links</h3>
            
            {shareLinks.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Link2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No share links yet</p>
                <p className="text-sm">Create one to share this workflow</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {shareLinks.map((link) => {
                  const expired = isExpired(link.expires_at);
                  const maxReached = isMaxAccessReached(link);
                  const inactive = !link.is_active || expired || maxReached;

                  return (
                    <div
                      key={link.id}
                      className={`p-3 border rounded-lg space-y-2 ${
                        inactive ? 'opacity-50 bg-muted/30' : 'bg-background'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <Link2 className="w-4 h-4 flex-shrink-0" />
                          <code className="text-xs bg-muted px-2 py-1 rounded truncate">
                            {window.location.origin}/shared/{link.token}
                          </code>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyShareLink(link.token)}
                            disabled={inactive}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openShareLink(link.token)}
                            disabled={inactive}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRevokeShareLink(link.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          <span>
                            {link.access_count}
                            {link.max_access_count && ` / ${link.max_access_count}`} views
                          </span>
                        </div>
                        {link.expires_at && (
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            <span>
                              {expired ? 'Expired' : 'Expires'} {formatDate(link.expires_at)}
                            </span>
                          </div>
                        )}
                        {!link.expires_at && (
                          <span className="text-green-600">Never expires</span>
                        )}
                      </div>

                      {inactive && (
                        <div className="text-xs text-red-600 font-medium">
                          {!link.is_active && 'Revoked'}
                          {expired && 'Expired'}
                          {maxReached && 'Max access reached'}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
